import React from 'react';
import Card from '../../common/Card/Card';
import { Place } from '../../../types/results.types';
import { Building2, Mail, Repeat2, Rows3, Star, Users } from 'lucide-react';
import { toTitleCase } from '../../../utils/formatting';
import OverviewTab from './OverviewTab';
import MatchEvidenceTab, { hasMatchEvidence } from './MatchEvidenceTab';
import PeopleTab from './PeopleTab';
import EmailsTab from './EmailsTab';
import ReviewsTab from './ReviewsTab';
import { usePostHog } from 'posthog-js/react';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { selectHeadCompletionForGlobalUi } from '../../../store/slices/dmUnlockSlice';
import EnrichmentCompletionBanner from '../shared/EnrichmentCompletionBanner';

type Tab = 'overview' | 'matchEvidence' | 'people' | 'emails' | 'reviews';

interface ResultsDetailProps {
  place: Place | null;
  searchId?: number;
  isDrawerOpen?: boolean;
  shortLabels?: Record<string, string>;
  onUpgradeClick?: () => void;
  currentPlan?: string | null;
  autoFindMode?: string | null;
  autoFindSuccessLimit?: number;
  autoFindSuccessCount?: number;
  autoFindAttemptCount?: number;
  autoFindNoEmailCount?: number;
  autoFindCatchAllCount?: number;
  autoFindGuardrailReason?: string | null;
}

const renderRatingStars = (rating: number | null) => {
  if (!rating) return null;
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  const starPath = 'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.173 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(full)].map((_, i) => (
        <svg key={`f${i}`} className="h-5 w-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path d={starPath} /></svg>
      ))}
      {half && (
        <div className="relative inline-block h-5 w-5">
          <svg className="absolute h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20"><path d={starPath} /></svg>
          <div className="absolute inset-0 w-1/2 overflow-hidden">
            <svg className="h-5 w-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path d={starPath} /></svg>
          </div>
        </div>
      )}
      {[...Array(empty)].map((_, i) => (
        <svg key={`e${i}`} className="h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20"><path d={starPath} /></svg>
      ))}
    </div>
  );
};

const ResultsDetail: React.FC<ResultsDetailProps> = ({
  place,
  searchId,
  isDrawerOpen = false,
  shortLabels = {},
  onUpgradeClick,
  currentPlan,
  autoFindMode,
  autoFindSuccessLimit,
  autoFindSuccessCount,
  autoFindAttemptCount,
  autoFindNoEmailCount,
  autoFindCatchAllCount,
  autoFindGuardrailReason,
}) => {
  const posthog = usePostHog();
  const { hasReviewsAccess } = useFeatureAccess();
  const [tab, setTab] = React.useState<Tab>('overview');
  const [showEnrichmentBanner, setShowEnrichmentBanner] = React.useState(true);
  const headEnrichmentCompletion = useAppSelector(selectHeadCompletionForGlobalUi);
  const previousPlaceIdRef = React.useRef<number | null>(null);
  const wasDrawerOpenRef = React.useRef(isDrawerOpen);

  React.useEffect(() => {
    setShowEnrichmentBanner(true);
  }, [place?.id]);

  const [showPeopleTabCoachmark, setShowPeopleTabCoachmark] = React.useState(false);
  const peopleTabCoachmarkStorageKey = 'webleads_results_drawer_people_tab_coachmark:v1';
  const peopleTabCoachmarkShownRef = React.useRef<Set<string>>(new Set());

  const filteredPeopleCount = place?.people?.filter(
    person => person.ai_score !== null && person.ai_score !== undefined && person.ai_score <= 3
  ).length ?? 0;

  const emailCount =
    (place?.company_emails?.length ?? 0) +
    (place?.people?.flatMap(person => person.emails ?? []).length ?? 0);
  const showMatchEvidenceTab = place ? hasMatchEvidence(place) : false;
  const availableTabKeys = React.useMemo<Tab[]>(() => [
    'overview',
    ...(showMatchEvidenceTab ? ['matchEvidence' as Tab] : []),
    'people',
    'emails',
    ...(hasReviewsAccess ? ['reviews' as Tab] : []),
  ], [showMatchEvidenceTab, hasReviewsAccess]);

  const tabs: { key: Tab; icon: React.ComponentType<{ className?: string }>; label: string; count?: number }[] = [
    { key: 'overview', icon: Building2, label: 'Overview' },
    ...(showMatchEvidenceTab ? [{ key: 'matchEvidence' as Tab, icon: Rows3, label: 'Match Evidence' }] : []),
    { key: 'people', icon: Users, label: 'People & Emails', count: filteredPeopleCount },
    { key: 'emails', icon: Mail, label: 'Company Inbox', count: emailCount },
    ...(hasReviewsAccess ? [{ key: 'reviews' as Tab, icon: Star, label: 'Reviews', count: place?.review_count || undefined }] : []),
  ];

  React.useEffect(() => {
    const currentPlaceId = place?.id ?? null;
    const previousPlaceId = previousPlaceIdRef.current;
    const wasDrawerOpen = wasDrawerOpenRef.current;
    const openedFresh = isDrawerOpen && !wasDrawerOpen;
    const selectedDifferentPlace = currentPlaceId !== null && previousPlaceId !== null && currentPlaceId !== previousPlaceId;

    if (currentPlaceId !== null) {
      previousPlaceIdRef.current = currentPlaceId;
    }
    wasDrawerOpenRef.current = isDrawerOpen;

    if (openedFresh || selectedDifferentPlace || !availableTabKeys.includes(tab)) {
      setTab('overview');
    }
  }, [availableTabKeys, isDrawerOpen, place?.id, tab]);

  const dismissPeopleTabCoachmark = React.useCallback((action: 'got_it' | 'people_tab_clicked') => {
    setShowPeopleTabCoachmark(false);
    localStorage.setItem(peopleTabCoachmarkStorageKey, 'dismissed');
    posthog?.capture('drawer_people_tab_coachmark_dismissed', {
      search_id: searchId,
      action,
    });
  }, [posthog, searchId]);

  React.useEffect(() => {
    if (!isDrawerOpen) {
      setShowPeopleTabCoachmark(false);
      return;
    }

    if (filteredPeopleCount <= 0) {
      setShowPeopleTabCoachmark(false);
      return;
    }

    if (localStorage.getItem(peopleTabCoachmarkStorageKey) === 'dismissed') {
      setShowPeopleTabCoachmark(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setShowPeopleTabCoachmark(true);
      const placeEventKey = `${place?.id ?? 'none'}:${searchId ?? 'none'}`;
      if (!peopleTabCoachmarkShownRef.current.has(placeEventKey)) {
        peopleTabCoachmarkShownRef.current.add(placeEventKey);
        posthog?.capture('drawer_people_tab_coachmark_shown', { search_id: searchId });
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [filteredPeopleCount, isDrawerOpen, place?.id, peopleTabCoachmarkStorageKey, posthog, searchId]);

  if (!place) {
    return (
      <Card>
        <div className="py-12 text-center">
          <svg className="mx-auto mb-4 h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900">No business selected</h3>
          <p className="mt-1 text-gray-500">Select a business from the list to view details.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {showEnrichmentBanner && headEnrichmentCompletion && (
        <EnrichmentCompletionBanner
          completion={headEnrichmentCompletion}
          onDismiss={() => setShowEnrichmentBanner(false)}
          onVerifyClick={() => setTab('people')}
        />
      )}

      <section className="rounded-[24px] border border-[#EEE6E1] bg-white px-5 py-5 shadow-[0_12px_32px_rgba(138,48,51,0.06)]">
        <div className="mb-5 border-b border-[#F3ECE8] pb-5">
          <h2 id="drawer-title" className="mb-3 text-2xl font-bold leading-tight tracking-tight text-[#2D3142]">
            {toTitleCase(place.name)}
          </h2>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              {place.review_rating && (
                <div className="flex items-center gap-2">
                  {renderRatingStars(place.review_rating)}
                  <span className="text-sm text-gray-600">
                    {place.review_rating?.toFixed(1)} · {place.review_count} reviews
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {place.main_industry && (
                <span className="inline-flex items-center rounded-md bg-[#8A3033] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white">
                  {place.main_industry}
                </span>
              )}
              {place.is_previously_seen && (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-[#E4DBD6] bg-[#F5F1EF] px-2.5 py-1 text-xs font-medium text-gray-700"
                  role="status"
                  title="This business appeared in a previous search"
                >
                  <Repeat2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Previously Seen
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-[18px] border border-[#EEE6E1] bg-[#FFFEFD]">
          <div
            className="grid min-w-[520px]"
            style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
          >
            {tabs.map(currentTab => {
              const active = tab === currentTab.key;
              const Icon = currentTab.icon;

              return (
                <div key={currentTab.key} className={currentTab.key === 'people' ? 'relative' : undefined}>
                  <button
                    onClick={() => {
                      if (currentTab.key === 'people' && showPeopleTabCoachmark) {
                        dismissPeopleTabCoachmark('people_tab_clicked');
                      }
                      setTab(currentTab.key);
                    }}
                    className={`relative flex w-full items-center justify-center gap-2 px-3 py-3 text-xs transition-colors ${
                      active ? 'font-semibold text-[#8A3033]' : 'text-[#7B7280] hover:text-[#2D3142]'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="whitespace-nowrap">{currentTab.label}</span>
                    {currentTab.count !== undefined && currentTab.count > 0 && (
                      <span className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                        active ? 'bg-[#8A3033] text-white' : 'bg-[#F1ECE9] text-[#7B7280]'
                      }`}>
                        {currentTab.count}
                      </span>
                    )}
                    {active && <span className="absolute inset-x-5 bottom-0 h-0.5 rounded-full bg-[#8A3033]" />}
                  </button>
                  {currentTab.key === 'people' && showPeopleTabCoachmark && (
                    <div className="absolute left-1/2 top-[calc(100%+10px)] z-20 w-64 max-w-[min(16rem,calc(100vw-3rem))] -translate-x-1/2 rounded-xl bg-[#2D3142] p-4 text-white shadow-xl">
                      <div className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 bg-[#2D3142]" />
                      <p className="mb-1 text-sm font-bold">People tab</p>
                      <p className="text-xs leading-relaxed opacity-90">
                        Open People to see decision makers and their email addresses.
                      </p>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          dismissPeopleTabCoachmark('got_it');
                        }}
                        className="mt-3 rounded-md bg-white/15 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/25"
                      >
                        Got it
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {tab === 'overview' && (
        <OverviewTab
          place={place}
          searchId={searchId}
          shortLabels={shortLabels}
          onViewMatchEvidence={() => setTab('matchEvidence')}
          onViewPeople={() => setTab('people')}
        />
      )}
      {tab === 'matchEvidence' && <MatchEvidenceTab place={place} searchId={searchId} shortLabels={shortLabels} />}
      {tab === 'people' && (
        <PeopleTab
          place={place}
          searchId={searchId}
          onUpgradeClick={onUpgradeClick}
          currentPlan={currentPlan}
          autoFindMode={autoFindMode}
          autoFindSuccessLimit={autoFindSuccessLimit}
          autoFindSuccessCount={autoFindSuccessCount}
          autoFindAttemptCount={autoFindAttemptCount}
          autoFindNoEmailCount={autoFindNoEmailCount}
          autoFindCatchAllCount={autoFindCatchAllCount}
          autoFindGuardrailReason={autoFindGuardrailReason}
        />
      )}
      {tab === 'emails' && <EmailsTab place={place} searchId={searchId} />}
      {tab === 'reviews' && hasReviewsAccess && <ReviewsTab place={place} searchId={searchId} />}
    </div>
  );
};

export default ResultsDetail;
